import React, { useState, useEffect, useCallback } from 'react';
import { useGame } from '../context/GameContext';
import * as api from '../api';

const STATION_DATA = {
  mining: { icon: '⛏', name: 'Mining Station', desc: 'Extracts iron ore from nearby asteroids' },
  refinery: { icon: '🔥', name: 'Refinery', desc: 'Processes raw ore into refined plates' },
  shipyard: { icon: '🚀', name: 'Shipyard', desc: 'Constructs and maintains fleet vessels' },
  trading_post: { icon: '📡', name: 'Trading Post', desc: 'Facilitates market connections' },
  research_lab: { icon: '🔬', name: 'Research Lab', desc: 'Develops advanced technologies' },
};

function StationPanel() {
  const { stations, stationEffects, refreshStations, updateInventory } = useGame();
  const [msg, setMsg] = useState(null);
  const [msgType, setMsgType] = useState('info');
  const [upgradeCosts, setUpgradeCosts] = useState({});
  const [stationBuildCosts, setStationBuildCosts] = useState(null);
  const [convertAmount, setConvertAmount] = useState('');

  const loadCosts = useCallback(async () => {
    const costs = {};
    for (const s of stations) {
      try {
        const { data } = await api.getStationUpgradeCost(s.type);
        costs[s.type] = data;
      } catch { /* ignore */ }
    }
    setUpgradeCosts(costs);
  }, [stations]);

  useEffect(() => { loadCosts(); }, [loadCosts]);

  useEffect(() => {
    api.getStationBuildCosts().then(({ data }) => setStationBuildCosts(data.stations)).catch(() => {});
  }, []);

  // Load station effects on mount
  useEffect(() => { refreshStations(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showMsg = (text, type = 'info') => {
    setMsg(text);
    setMsgType(type);
    setTimeout(() => setMsg(null), 4000);
  };

  const handleUpgrade = async (type) => {
    try {
      const { data } = await api.upgradeStation(type);
      if (data.inventory) updateInventory(data.inventory);
      await refreshStations();
      await loadCosts();
      showMsg(`${STATION_DATA[type]?.name || type} upgraded!`, 'success');
    } catch (err) {
      showMsg(err.response?.data?.error || 'Upgrade failed', 'error');
    }
  };

  const handleCollect = async (type) => {
    try {
      const { data } = await api.collectStation(type);
      if (data.inventory) updateInventory(data.inventory);
      await refreshStations();
      const collected = data.collected || {};
      const entries = Object.entries(collected);
      if (entries.length > 0 && entries[0][1] > 0) {
        const str = entries.map(([k, v]) => `+${v.toLocaleString()} ${k.replace(/_/g, ' ')}`).join(', ');
        showMsg(str, 'success');
      } else {
        showMsg('Storage empty — check back later', 'info');
      }
    } catch (err) {
      showMsg(err.response?.data?.error || 'Collection failed', 'error');
    }
  };

  const handleBuildStation = async (type) => {
    try {
      const { data } = await api.buildStation(type);
      if (data.inventory) updateInventory(data.inventory);
      await refreshStations();
      showMsg(`${STATION_DATA[type]?.name || type} built!`, 'success');
    } catch (err) {
      showMsg(err.response?.data?.error || 'Build failed', 'error');
    }
  };

  const handleRefineryConvert = async (e) => {
    e.preventDefault();
    const amt = Number(convertAmount);
    if (!amt || amt <= 0) return;
    try {
      const { data } = await api.refineryConvert(amt);
      if (data.inventory) updateInventory(data.inventory);
      showMsg(`Converted ${data.input_consumed?.toLocaleString()} ${fmtResource(data.input)} → +${data.output_produced?.toLocaleString()} ${fmtResource(data.output)}`, 'success');
      setConvertAmount('');
    } catch (err) {
      showMsg(err.response?.data?.error || 'Conversion failed', 'error');
    }
  };

  const fmtResource = (name) => (name || '').replace(/_/g, ' ');

  const hasRefinery = stations.some(s => s.type === 'refinery');
  const refineryInfo = stationEffects?.refinery;
  const refineryRatio = refineryInfo?.inputPerOutput;

  return (
    <div className="panel station-panel">
      {msg && <div className={`hud-toast hud-toast-${msgType}`}>{msg}</div>}

      {/* Station Effects Summary */}
      {stationEffects && (
        <div className="station-effects-bar">
          {stationEffects.tradingPost && (
            <span className="effect-item" title="Trading Post price bonus on sells">
              📡 Sell Bonus: <strong>+{Math.round((stationEffects.tradingPost.priceBonus - 1) * 100)}%</strong>
            </span>
          )}
          {stationEffects.shipyard && (
            <span className="effect-item" title="Max fleet from Shipyard">
              🚀 Max Ships: <strong>{stationEffects.shipyard.maxShips}</strong> / Drones: <strong>{stationEffects.shipyard.maxDrones}</strong>
            </span>
          )}
          {stationEffects.researchLab && (
            <span className="effect-item" title="Max upgrade level from Research Lab">
              🔬 Max Upgrade LV: <strong>{stationEffects.researchLab.maxUpgradeLevel}</strong>
            </span>
          )}
          {stationEffects.refinery && (
            <span className="effect-item" title="Refinery conversion rate">
              🔥 Convert: <strong>{stationEffects.refinery.inputPerOutput}</strong> {fmtResource(stationEffects.refinery.input)} → 1 {fmtResource(stationEffects.refinery.output)}
            </span>
          )}
        </div>
      )}

      <section className="section">
        <h3 className="section-title">
          <span className="title-accent">⬡</span> ORBITAL STATIONS
        </h3>
        <div className="hud-grid station-grid">
          {stations.map(s => {
            const info = STATION_DATA[s.type] || { icon: '🏗', name: s.type, desc: '' };
            const costData = upgradeCosts[s.type];
            const cost = costData?.cost;
            const currentOutput = costData?.currentOutput;
            const nextOutput = costData?.nextOutput;
            const sinceCollect = s.last_collected
              ? Math.round((Date.now() - new Date(s.last_collected).getTime()) / (1000 * 60))
              : 0;
            const hours = Math.floor(sinceCollect / 60);
            const mins = sinceCollect % 60;
            const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;

            return (
              <div key={s.id} className="hud-card station-card">
                <div className="station-header">
                  <span className="station-icon">{info.icon}</span>
                  <div>
                    <div className="card-name">{info.name}</div>
                    <div className="card-desc-sm">{info.desc}</div>
                  </div>
                </div>
                <div className="station-stats">
                  <div className="station-level">
                    <span className="level-label">LEVEL</span>
                    <span className="level-num">{s.level}</span>
                  </div>
                  {currentOutput && (
                    <div className="station-timer">
                      <span className="timer-label">OUTPUT/HR</span>
                      <span className="timer-val">+{currentOutput.amount} {fmtResource(currentOutput.resource)}</span>
                    </div>
                  )}
                  {s.last_collected && (
                    <div className="station-timer">
                      <span className="timer-label">ACCUMULATED</span>
                      <span className="timer-val">{timeStr}</span>
                    </div>
                  )}
                </div>

                {/* Upgrade info */}
                {cost && (
                  <div className="station-upgrade-info">
                    <div className="upgrade-cost-row">
                      <span className="upgrade-heading">UPGRADE TO LV {s.level + 1}</span>
                    </div>
                    <div className="upgrade-cost-row">
                      <span className="stat-label">COST</span>
                      <span className="stat-value">
                        {Object.entries(cost).filter(([, v]) => v > 0).map(([k, v]) =>
                          `${v.toLocaleString()} ${k.replace(/_/g, ' ')}`
                        ).join(' · ') || 'Free'}
                      </span>
                    </div>
                    {nextOutput && (
                      <div className="upgrade-cost-row">
                        <span className="stat-label">NEW OUTPUT</span>
                        <span className="stat-value upgrade-benefit">
                          +{nextOutput.amount} {fmtResource(nextOutput.resource)}/hr
                          {currentOutput && nextOutput.amount > currentOutput.amount && (
                            <span className="upgrade-diff"> (+{nextOutput.amount - currentOutput.amount})</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="station-actions">
                  <button className="btn btn-collect" onClick={() => handleCollect(s.type)}>
                    COLLECT
                  </button>
                  <button className="btn btn-upgrade" onClick={() => handleUpgrade(s.type)}>
                    UPGRADE {cost ? `(${Object.entries(cost).filter(([,v]) => v > 0).map(([k,v]) => `${v} ${k.replace(/_/g,' ')}`).join(', ')})` : ''}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Refinery Conversion */}
      {hasRefinery && refineryRatio && (
        <section className="section">
          <h3 className="section-title">
            <span className="title-accent">🔥</span> REFINERY CONVERSION
          </h3>
          <div className="refinery-convert-card hud-card">
            <p className="refinery-desc">
              Convert {fmtResource(refineryInfo.input)} into {fmtResource(refineryInfo.output)}. Current rate: <strong>{refineryRatio} {fmtResource(refineryInfo.input)} = 1 {fmtResource(refineryInfo.output)}</strong>.
              Upgrade your Refinery to improve the ratio.
            </p>
            <form className="refinery-form" onSubmit={handleRefineryConvert}>
              <div className="form-group">
                <label className="hud-label">{fmtResource(refineryInfo.input)} to Convert</label>
                <input
                  className="hud-input"
                  type="number"
                  min={refineryRatio}
                  step={refineryRatio}
                  placeholder={`Min ${refineryRatio}`}
                  value={convertAmount}
                  onChange={e => setConvertAmount(e.target.value)}
                  required
                />
              </div>
              {convertAmount > 0 && (
                <div className="refinery-preview">
                  {Number(convertAmount).toLocaleString()} {fmtResource(refineryInfo.input)} → {Math.floor(Number(convertAmount) / refineryRatio).toLocaleString()} {fmtResource(refineryInfo.output)}
                </div>
              )}
              <button type="submit" className="btn btn-glow">CONVERT</button>
            </form>
          </div>
        </section>
      )}

      {/* Build New Stations */}
      {stationBuildCosts && (() => {
        const ownedTypes = stations.map(s => s.type);
        const available = Object.entries(stationBuildCosts).filter(([type]) => !ownedTypes.includes(type));
        if (available.length === 0) return null;
        return (
          <section className="section">
            <h3 className="section-title">
              <span className="title-accent">+</span> BUILD STATION
            </h3>
            <div className="hud-grid">
              {available.map(([type, cost]) => {
                const info = STATION_DATA[type] || { icon: '🏭', name: type, desc: '' };
                return (
                  <div key={type} className="hud-card build-card">
                    <div className="station-header">
                      <span className="station-icon">{info.icon}</span>
                      <div>
                        <div className="card-name">{info.name}</div>
                        <div className="card-desc-sm">{info.desc}</div>
                      </div>
                    </div>
                    <div className="build-cost">
                      {Object.entries(cost).filter(([, v]) => v > 0).map(([k, v]) => (
                        <span key={k} className="cost-item">{v.toLocaleString()} {k.replace(/_/g, ' ')}</span>
                      ))}
                    </div>
                    <button className="btn btn-build" onClick={() => handleBuildStation(type)}>BUILD</button>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}
    </div>
  );
}

export default StationPanel;
